/**
 * `/settings/secrets` — tenant secrets management page (RLY-026).
 *
 * AC summary:
 *   - Lists existing secrets (name + created_at + optional rotated_at).
 *   - "Add secret" button opens a dialog with name + value fields. The value
 *     field uses `type=password`. On submit → POST → toast + cache
 *     invalidation + dialog close. **Defense-in-depth:** the value field is
 *     cleared on success so the plaintext doesn't linger in browser memory
 *     beyond the round-trip.
 *   - Each row has a delete button → confirmation dialog → DELETE → toast +
 *     invalidation.
 *   - Empty state when no secrets exist.
 *   - Role enforcement on the frontend: viewers see the page but the "Add
 *     secret" button is disabled with a tooltip per AC #5. The backend
 *     (RLY-023 AC #5) enforces the same in 403 form for defense-in-depth.
 *
 * **Hard rule (RLY-023):** values are never returned by the API after
 * creation. The list-renderer below treats each row as `{ name, created_at,
 * rotated_at?, metadata }` — there is no place in the UI for a `value`
 * field. The Sprint-07 §"Pair B" `expectTypeOf` assertions in
 * `use-secrets-shape.test.ts` pin this at compile time.
 *
 * Cross-package wires (per [12]):
 *   - Schemas: `SecretListResponseSchema`, `CreateSecretRequestSchema`,
 *     `SecretCreateResponseSchema` from `@simpliwork/api-schema`
 *     (`packages/api-schema/src/secrets.ts:56`, `:66`, `:83`).
 *   - API endpoints: `apps/portal-api/src/routes/secrets.ts:191` (POST),
 *     `:220` (GET), `:266` (DELETE) — RLY-023 producer.
 *
 * Reuse (per Sprint-07 frontend-dev brief): the RLY-024 hand-rolled-shadcn
 * primitives are in place — Card / Dialog / Input / Label / Button / Badge /
 * Skeleton / Toast.
 */
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { useMe } from "@/hooks/use-me";
import { useCreateSecret, useDeleteSecret, useSecretsList } from "@/hooks/use-secrets";
import { ApiError } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

/**
 * Roles permitted to write or delete secrets (mirrors the backend
 * `WRITE_ROLES` set at `apps/portal-api/src/routes/secrets.ts:76`). The
 * backend enforces this in 403 form; we mirror it here for UX (disabled
 * button + tooltip) per AC #5.
 *
 * RLY-253 closure: `MeResponseSchema.role` (api-schema producer at
 * `packages/api-schema/src/tenants.ts:63-67`) ships the `owner | admin |
 * member` API-side vocab per ADR-015 and `TenantRoleSchema`. The portal-api
 * `/api/v1/me` handler at `apps/portal-api/src/routes/me.ts:110` populates
 * `role` from the tenant-context middleware (`ctx.role`, projected from
 * the four-value DB-side `tenant_members.role` via `mapDbRoleToApiRole()`).
 * The hook below now reads the real role and returns `true` only for
 * `owner` or `admin` — closes the RLY-026 [06] documented viewer-role
 * variance.
 *
 * Note: API-side vocab is three-valued (`owner | admin | member`) per
 * ADR-015. The DB-side `viewer` projects to `member` at the boundary, so
 * the hook predicate gates on the API-side write-roles set rather than
 * negating `viewer` — same outcome, but reads true at every layer.
 */
function useCanWriteSecrets(): boolean {
  const me = useMe();
  // While the `me` query is loading or errored, fall back to the
  // safe-deny default (the backend's 403 still gates the mutation).
  if (!me.data) return false;
  const role = me.data.role;
  return role === "owner" || role === "admin";
}

export function SettingsSecretsPage() {
  const list = useSecretsList();
  const create = useCreateSecret();
  const remove = useDeleteSecret();
  const toast = useToast();

  // Per RLY-253 closure: `useCanWriteSecrets` consumes `useMe().data.role`
  // directly; the page no longer needs to `useMe()` for itself.
  const canWrite = useCanWriteSecrets();
  const writeDisabledTooltip = canWrite ? undefined : "Only owners and admins can create secrets.";

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>("TWILIO_ACCOUNT_SID");
  const [customName, setCustomName] = useState("");
  const [value, setValue] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  const name = selectedKey === "__custom__" ? customName : selectedKey;

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  function resetCreateForm() {
    setSelectedKey("TWILIO_ACCOUNT_SID");
    setCustomName("");
    // Defense-in-depth — do NOT keep plaintext lingering in component state.
    setValue("");
    setNameError(null);
  }

  function openCreate() {
    resetCreateForm();
    setCreateOpen(true);
  }

  function closeCreate() {
    setCreateOpen(false);
    resetCreateForm();
  }

  async function onCreateSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedValue = value;
    if (trimmedName.length === 0) {
      setNameError("Name is required.");
      return;
    }
    // Schema regex (`packages/api-schema/src/secrets.ts:31`) — alphanum + `_-`,
    // 1-64 chars. Trip-wire here so the user gets feedback before round-trip.
    if (!/^[A-Za-z0-9](?:[A-Za-z0-9_-]{0,62}[A-Za-z0-9])?$/.test(trimmedName)) {
      setNameError(
        "Name must be 1-64 characters of letters, digits, dashes, or underscores; cannot start or end with punctuation.",
      );
      return;
    }
    if (trimmedValue.length === 0) {
      setNameError("Value is required.");
      return;
    }
    try {
      await create.mutateAsync({ name: trimmedName, value: trimmedValue });
      toast.toast({
        title: "Secret saved",
        description: `'${trimmedName}' is now available to agents in this workspace.`,
        variant: "success",
      });
      // **Per RLY-026 AC + defense-in-depth**: clear the value field on
      // success so plaintext doesn't sit in component state beyond the
      // round-trip. The `closeCreate` already calls `resetCreateForm`;
      // this is a belt-and-suspenders wipe in case the dialog stays open.
      setValue("");
      closeCreate();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({ title: "Could not save secret", description: message, variant: "error" });
    }
  }

  async function onConfirmDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      await remove.mutateAsync({ name: target });
      toast.toast({
        title: "Secret deleted",
        description: `'${target}' removed from this workspace.`,
        variant: "success",
      });
      setDeleteTarget(null);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.payload.message
          : err instanceof Error
            ? err.message
            : "Unknown error";
      toast.toast({ title: "Could not delete secret", description: message, variant: "error" });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="secrets-heading">
            Secrets
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Secrets are encrypted at rest with a per-tenant DEK. Values are never returned by the
            API after creation — to rotate, save the secret again with the same name.
          </p>
        </div>
        <Button
          data-testid="secrets-add-cta"
          onClick={openCreate}
          disabled={!canWrite}
          {...(writeDisabledTooltip ? { title: writeDisabledTooltip } : {})}
        >
          New secret
        </Button>
      </header>

      <Card data-testid="secrets-card">
        <CardHeader>
          <CardTitle>Stored secrets</CardTitle>
          <CardDescription>
            Names + creation timestamps; values are never displayed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {list.isLoading && (
            <div className="flex flex-col gap-2" data-testid="secrets-loading">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {list.error && (
            <p data-testid="secrets-error" className="text-sm text-destructive">
              Couldn't load secrets: {list.error.message}
            </p>
          )}
          {list.data && list.data.items.length === 0 && (
            <div data-testid="secrets-empty" className="flex flex-col items-start gap-3">
              <p className="text-sm text-muted-foreground">You haven't added any secrets yet.</p>
              {canWrite && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="secrets-empty-cta"
                  onClick={openCreate}
                >
                  Add your first secret
                </Button>
              )}
            </div>
          )}
          {list.data && list.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="secrets-table">
                <thead>
                  <tr className="border-b text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th scope="col" className="py-2 pr-4">
                      Name
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Created
                    </th>
                    <th scope="col" className="py-2 pr-4">
                      Last rotated
                    </th>
                    <th scope="col" className="py-2 text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((item) => (
                    <tr
                      key={item.name}
                      className="border-b last:border-0"
                      data-testid={`secret-row-${item.name}`}
                    >
                      <td className="py-2 pr-4">
                        <span className="font-mono text-xs">{item.name}</span>
                        {/*
                          Defense-in-depth: do NOT render anything that could
                          imply a `value` exists in the response. The schema
                          guarantees the field is absent; the UI mirrors that.
                        */}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="text-xs text-muted-foreground">
                          {formatTimestamp(item.created_at)}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        {item.rotated_at ? (
                          <Badge variant="secondary" data-testid={`secret-rotated-${item.name}`}>
                            {formatTimestamp(item.rotated_at)}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`secret-delete-${item.name}`}
                          onClick={() => setDeleteTarget(item.name)}
                          disabled={!canWrite}
                          {...(writeDisabledTooltip ? { title: writeDisabledTooltip } : {})}
                          aria-label={`Delete secret ${item.name}`}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => (open ? setCreateOpen(true) : closeCreate())}
      >
        <form onSubmit={onCreateSubmit} data-testid="secrets-create-form">
          <DialogHeader>
            <DialogTitle>New secret</DialogTitle>
            <DialogDescription>
              Saved secrets are referenced by name in agent configuration. The value is encrypted
              and never returned by the API after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="secrets-create-name">Secret</Label>
              <select
                id="secrets-create-name"
                data-testid="secrets-create-name"
                value={selectedKey}
                onChange={(e) => {
                  setSelectedKey(e.target.value);
                  if (nameError) setNameError(null);
                }}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {/* M1.7 — LLM provider keys are operator secrets at the
                    Worker level; tenants no longer supply them.
                    docs/MONETIZATION_ROADMAP.md / docs/runbooks/byok-teardown.md */}
                <optgroup label="Twilio">
                  <option value="TWILIO_ACCOUNT_SID">Twilio Account SID</option>
                  <option value="TWILIO_AUTH_TOKEN">Twilio Auth Token</option>
                  <option value="TWILIO_FROM_NUMBER">Twilio From Number</option>
                </optgroup>
                <optgroup label="Meta / Facebook">
                  <option value="META_APP_ID">Meta App ID</option>
                  <option value="META_APP_SECRET">Meta App Secret</option>
                  <option value="META_ACCESS_TOKEN">Meta Access Token</option>
                </optgroup>
                <optgroup label="LinkedIn">
                  <option value="LINKEDIN_CLIENT_ID">LinkedIn Client ID</option>
                  <option value="LINKEDIN_CLIENT_SECRET">LinkedIn Client Secret</option>
                </optgroup>
                <optgroup label="Twitter / X">
                  <option value="TWITTER_API_KEY">Twitter/X API Key</option>
                  <option value="TWITTER_API_SECRET">Twitter/X API Secret</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="__custom__">Custom secret name…</option>
                </optgroup>
              </select>
              {selectedKey === "__custom__" && (
                <Input
                  data-testid="secrets-create-custom-name"
                  value={customName}
                  onChange={(e) => {
                    setCustomName(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                  placeholder="MY_SECRET_KEY"
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-1 font-mono text-sm"
                />
              )}
              {selectedKey !== "__custom__" && (
                <p className="text-xs text-muted-foreground font-mono">{selectedKey}</p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="secrets-create-value">Value</Label>
              <Input
                id="secrets-create-value"
                data-testid="secrets-create-value"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="The secret value"
                autoComplete="new-password"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                You won't be able to view this value again after submitting.
              </p>
            </div>
            {nameError && (
              <p data-testid="secrets-create-error" className="text-sm text-destructive">
                {nameError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="secrets-create-cancel"
              onClick={closeCreate}
            >
              Cancel
            </Button>
            <Button type="submit" data-testid="secrets-create-submit" disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Save secret"}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogHeader>
          <DialogTitle>Delete secret?</DialogTitle>
          <DialogDescription>
            {deleteTarget ? (
              <>
                This will remove <span className="font-mono">'{deleteTarget}'</span> from this
                workspace. Agents that reference it by name will fail their next run until the
                secret is re-added. This action cannot be undone.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            data-testid="secrets-delete-cancel"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            data-testid="secrets-delete-confirm"
            onClick={onConfirmDelete}
            disabled={remove.isPending}
          >
            {remove.isPending ? "Deleting…" : "Delete secret"}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default SettingsSecretsPage;
